import { STORE_EVENTS } from '@/constants/storeEvents'
import {
  eventTimestamp,
  StoreEventParams,
  trackStoreEvent
} from '@/utils/storeEvents.utils'
import { capitalize } from '@/utils/string.utils'

type Slot = { start_time: string | null; end_time: string | null }

/** One weekday of the business hours form: times are local 'HH:mm'. */
export type BusinessHoursDay = Slot & {
  day_name: string
  isWorking: boolean
  slots: Slot[]
}

const hasBothTimes = (
  slot: Slot
): slot is { start_time: string; end_time: string } =>
  Boolean(slot.start_time && slot.end_time)

// What the event reports for a day: open or not, and every complete slot as
// 'HH:mm-HH:mm' (the day's main slot first, then the additional ones). A slot
// still missing a time is not a slot yet, and a closed day has none.
const describeDay = (day: BusinessHoursDay) => ({
  isOpen: day.isWorking,
  slots: day.isWorking
    ? [{ start_time: day.start_time, end_time: day.end_time }, ...day.slots]
        .filter(hasBothTimes)
        .map(slot => `${slot.start_time}-${slot.end_time}`)
    : []
})

/**
 * One STORE_BUSINESS_HOURS_CHANGED payload per day whose reported content
 * differs between `previous` and `next`. The form sends all 7 days on every
 * confirm, so the days the user did not touch (or touched and put back) give
 * nothing; a day with no previous state counts as changed. `timestamp` is left
 * to the caller, so all the days of one confirm share it.
 */
export const buildBusinessHoursEvents = (
  storeId: string | null | undefined,
  previous: BusinessHoursDay[],
  next: BusinessHoursDay[]
): StoreEventParams[] =>
  next.flatMap(day => {
    const before = previous.find(({ day_name }) => day_name === day.day_name)
    const { isOpen, slots } = describeDay(day)

    if (before) {
      const was = describeDay(before)
      if (was.isOpen === isOpen && was.slots.join(',') === slots.join(',')) {
        return []
      }
    }

    return [
      {
        store_id: storeId,
        day: capitalize(day.day_name),
        is_open: isOpen ? 'On' : 'Off',
        slots
      }
    ]
  })

/**
 * Report the business hours the store just saved: one event per changed day.
 * Call it once the API confirmed the save. Never throws, so it cannot turn a
 * successful save into an error toast.
 */
export function trackBusinessHoursChanges(
  storeId: string | null | undefined,
  previous: BusinessHoursDay[],
  next: BusinessHoursDay[]
): void {
  try {
    const timestamp = eventTimestamp()

    buildBusinessHoursEvents(storeId, previous, next).forEach(params =>
      trackStoreEvent(STORE_EVENTS.BUSINESS_HOURS_CHANGED, {
        ...params,
        timestamp
      })
    )
  } catch (error) {
    console.warn('Business hours event failed', error)
  }
}
